import { Link } from 'react-router';
import type { DormantClientItem } from '../../types/dashboard';

interface DormantClientsProps {
  clients: DormantClientItem[];
  count: number;
  loading?: boolean;
  error?: boolean;
  forbidden?: boolean;
  onRetry?: () => void;
}

function formatDaysAgo(days: number | null): string {
  if (days === null) return 'unknown';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

function urgencyColor(days: number | null): string {
  if (days === null) return 'text-ink-4';
  if (days >= 90) return 'text-rust';
  if (days >= 60) return 'text-amber-600';
  return 'text-ink-3';
}

export function DormantClients({
  clients,
  count,
  loading = false,
  error = false,
  forbidden = false,
  onRetry,
}: DormantClientsProps) {

  // Forbidden state
  if (forbidden) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <h3 className="text-[14px] font-semibold">At-risk clients</h3>
        </div>
        <div className="px-4 pt-4 pb-3 flex items-center justify-center min-h-[120px]">
          <p className="text-ink-4 text-[13px]">You don't have access to this.</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <h3 className="text-[14px] font-semibold">At-risk clients</h3>
        </div>
        <div className="px-4 pt-4 pb-3">
          <p className="text-[13px] text-ink-4 mb-3">Couldn't load client data.</p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="text-[11.5px] font-medium text-tide hover:text-tide/80"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  // Loading skeleton
  if (loading && clients.length === 0) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <div className="h-4 w-24 bg-canvas animate-pulse rounded" />
        </div>
        <div className="px-4 pt-3 pb-3 space-y-2.5">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center justify-between">
              <div className="h-3 w-28 bg-canvas animate-pulse rounded" />
              <div className="h-3 w-12 bg-canvas animate-pulse rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Empty state: no dormant clients (never while still loading — that would
  // assert an all-clear before the data has arrived).
  if (!loading && (count === 0 || clients.length === 0)) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <h3 className="text-[14px] font-semibold">At-risk clients</h3>
        </div>
        <div className="px-4 pt-4 pb-3 flex flex-col items-center justify-center min-h-[120px] gap-2">
          <p className="text-[11.5px] text-ink-4">
            All clients have recent activity. No one at risk.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 hair-b">
        <div className="flex items-center justify-between">
          <h3 className="text-[14px] font-semibold">At-risk clients</h3>
          <span className="text-[11.5px] text-ink-4 num">
            {count} client{count !== 1 ? 's' : ''} dormant
          </span>
        </div>
      </div>

      {/* Client rows */}
      <div className="px-4 pt-3 pb-3 space-y-2">
        {clients.map((client) => (
          <div key={client.customerId} className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              {client.clientId ? (
                <Link
                  to={`/clients/${client.clientId}`}
                  className="text-[12px] text-ink-2 truncate hover:text-tide"
                  title={client.name}
                >
                  {client.name}
                </Link>
              ) : (
                <span className="text-[12px] text-ink-2 truncate" title={client.name}>
                  {client.name}
                </span>
              )}
              <span className="text-[10.5px] text-ink-4 num shrink-0">
                {client.bookingCount} booking{client.bookingCount !== 1 ? 's' : ''}
              </span>
            </div>
            <span className={`text-[11.5px] font-medium num shrink-0 ml-2 ${urgencyColor(client.daysSinceLast)}`}>
              {formatDaysAgo(client.daysSinceLast)}
            </span>
          </div>
        ))}

        {count > clients.length && (
          <p className="text-[10.5px] text-ink-4 pt-1">
            and {count - clients.length} more
          </p>
        )}
      </div>
    </div>
  );
}
